import { LockClosedIcon } from '@heroicons/react/24/outline'
import { EditPencil } from 'iconoir-react'
import { KeyboardEvent, ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { Tooltip } from 'app/components/Tooltip'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { useEscapeListener } from 'app/hooks/useEscapeListener'
import { sanitizeName } from 'app/sanitize-name'
import { logError } from 'lib/logging'

type Props = {
  name?: string
  nameSuffix?: ReactNode
  isLocked: boolean
  isReadOnly?: boolean
  onRename: (name: string) => Promise<void>
}

export function ContactNameEditor({ name, nameSuffix, isLocked, isReadOnly = false, onRename }: Props): ReactElement {
  const displayName = sanitizeName(name)
  const [draft, setDraft] = useState<string>()
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Enter and blur both call commit(); settledRef makes the second call a no-op once one of
  // them (or Escape) has already settled this edit.
  const settledRef = useRef(false)
  // The raw (unsanitized) value the input was seeded with, so a mid-edit refetch that changes
  // `name` can't make an untouched draft look unchanged, or look changed, against a moving target.
  const seededRef = useRef(``)
  const isEditing = draft !== undefined

  const cancel = () => {
    settledRef.current = true
    setDraft(undefined)
  }

  useEscapeListener(cancel, isEditing)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  const startEditing = () => {
    settledRef.current = false
    const seeded = name?.trim() ?? ``
    seededRef.current = seeded
    setDraft(seeded)
  }

  const commit = async () => {
    if (settledRef.current) return
    settledRef.current = true
    const nextName = draft?.trim()
    if (nextName && nextName !== seededRef.current) {
      setSaving(true)
      try {
        await onRename(nextName)
      } catch (error) {
        logError(`Error renaming contact`, error)
      } finally {
        setSaving(false)
        setDraft(undefined)
      }
    } else {
      setDraft(undefined)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== `Enter` || event.nativeEvent.isComposing) return
    event.preventDefault()
    commit()
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        aria-label="Contact name"
        // Takes the heading's line height and type, so the header row and its actions don't jump
        // and the name doesn't shrink when editing starts.
        className="h-10 min-w-60 px-2 py-0 font-[inherit] text-[length:inherit] tracking-[inherit]"
        value={draft}
        disabled={saving}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
      />
    )
  }

  return (
    <span className="inline-flex items-center gap-1">
      <span>
        {displayName}
        {nameSuffix}
      </span>
      {!isReadOnly &&
        (isLocked ? (
          <Tooltip message="Name comes from the PMS. Update the patient there to change it here.">
            <span role="img" aria-label="Name locked" className="inline-flex">
              <LockClosedIcon className="size-4 text-minimal" />
            </span>
          </Tooltip>
        ) : (
          <Button variant="icon" size="icon-sm" aria-label="Rename contact" onClick={startEditing}>
            <EditPencil className="size-4 text-subtle" />
          </Button>
        ))}
    </span>
  )
}
